import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { Card, Form, Spinner, Alert, Modal} from 'react-bootstrap';

export default function CharacterCards() {
  const { currentUser } = useAuth();
  const [characters, setCharacters] = useState([]);
  const [unlocked, setUnlocked] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [UNLOCK_ALL, setUNLOCK_ALL] = useState(false); // Now state

  //const UNLOCK_ALL = true;

  useEffect(() => {
    // Fetch "visibility" from "siteManagement/characters"
    const fetchVisibility = async () => {
      try {
        const docRef = doc(db, 'siteManagement', 'characters');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setUNLOCK_ALL(!!docSnap.data().visible);
        } else {
          console.log('No such document for site management!');
        }
      } catch (error) {
        console.error('Error fetching visibility:', error);
      }
    };
    fetchVisibility();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Always fetch all characters
        const charactersQuery = collection(db, 'characters');
        const charactersSnapshot = await getDocs(charactersQuery);
        let charsData = charactersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => a.order - b.order);

        // Filter by search term (case-insensitive)
        if (searchTerm) {
          const lowerSearch = searchTerm.toLowerCase();
          charsData = charsData.filter(char =>
            char.name.toLowerCase().includes(lowerSearch)
          );
        }

        // Get unlocks if logged in
        let unlockedData = [];
        if (currentUser?.uid) {
          const unlocksDoc = await getDoc(doc(db, 'userUnlocks', currentUser.uid));
          unlockedData = unlocksDoc.exists() ? unlocksDoc.data().unlockedCharacters : [];
        }

        setCharacters(charsData);
        setUnlocked(unlockedData);
        setError('');
      } catch (err) {
        console.error('Firestore error:', err);
        setError(`Error loading characters: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    // Real-time updates listener
    const unsubscribe = onSnapshot(collection(db, 'characters'), fetchData);

    // Initial fetch
    fetchData();

    return () => unsubscribe();
  }, [searchTerm, currentUser?.uid]);

  const isUnlocked = (character) => {
    if (UNLOCK_ALL) return true;
    if (!character.requiredUrl) return true;
    return unlocked.includes(character.id);
  };

  const handleClose = () => setSelectedCharacter(null);

  return (
    <div className="container">
      <div className="row pb-4" style={{ borderBottom: 'thin solid white', paddingTop: '54px' }}>
        <div className="col-md-6" style={{ textAlign: 'left', color: 'white' }}>
          <h2 className="mb-4">Tu colección de personajes</h2>
        </div>
        <div className="col-md-6">
          <Form.Control
            type="search"
            placeholder="Busca personajes por nombre"
            className="mb-4"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: '500px', margin: '0 auto' }}
          />

          {loading && (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          )}

          {error && <Alert variant="danger" className="text-center">{error}</Alert>}
        </div>
        <div className="col-md-12" style={{ textAlign: 'left', color: 'white' }}>
          <p>
            El inframundo está lleno de personajes únicos y fascinantes. Explora sus historias, descubre sus poderes y debilidades, y desbloquea nuevos personajes a medida que avanzas en la historia. ¡Escanea los códigos QR al final de cada capítulo del libro para desbloquearlos!
          </p>
        </div>
      </div>

      <div className="row g-4 mt-4">
        {characters.map((character) => (
          <div className='col-4 col-md-2' key={character.id}>
            <div className="position-relative" style={{ borderRadius: '5px'}}>
              <Card
                className={`mainCard shadow ${isUnlocked(character) ? 'hover-effect' : 'locked-card'}`}
                onClick={() => isUnlocked(character) && setSelectedCharacter(character)}
              >
                <div style={{
                  aspectRatio: '3 / 4',
                  width: '100%',
                  overflow: 'hidden',
                }}>
                  {character.imageUrl && (
                    <Card.Img
                      variant="top"
                      src={`${character.imageUrl}?v=${Date.now()}`}
                      alt={character.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: character.position,
                        cursor: isUnlocked(character) ? 'pointer' : 'default'
                      }}
                    />
                  )}
                </div>
                {/* ...rest of Card... */}
              </Card>

              {!isUnlocked(character) && (
                <div className="locked-overlay">
                  <div className="text-center p-2">
                    <p className="mb-0">
                      {character.requiredUrlText}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {!loading && characters.length === 0 && (
        <div className="text-center text-muted py-5">
          No characters found{searchTerm && ` matching "${searchTerm}"`}
        </div>
      )}

      {/* Image Modal */}
      <Modal
        show={!!selectedCharacter}
        onHide={handleClose}
        centered
        size="xl"
        className="image-modal"
      >
        <Modal.Body className="p-0" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
          {selectedCharacter && (
            <div className="d-flex flex-column">
              <div className="position-relative">
                <div
                  class="modal-card card"
                  style={{ maxWidth: '1200px' }}
                >
                  <div className="row g-0">
                    <div className="col-md-6" style={{ textAlign: 'center', padding: '40px' }}>
                      <img src={`${selectedCharacter.imageUrl}?v=${Date.now()}`}
                        className="card-img shadow"
                        alt={selectedCharacter.name}
                        style={{
                          maxHeight: '70vh',
                          objectPosition: 'center',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                    <div class="col-md-6">
                      <div class="card-body">
                        <h3 class="card-title">{selectedCharacter.name}</h3>
                        {selectedCharacter.subtitle && (
                          <p className="card-text Galindo text-muted mt-2 mb-0">{selectedCharacter.subtitle}</p>
                        )}
                        {selectedCharacter.description && (
                          <p className="card-text text-muted mt-2 mb-0">{selectedCharacter.description}</p>
                        )}
                        {selectedCharacter.description && (
                          <div 
                            className="card-text card-att text-muted mt-2 mb-0"
                            style={{ backgroundImage: 'url(https://diadelavida.com/images/layout/cards_att_positive.png)' }}
                          >
                            <span>Superpoderes</span><br />
                            {selectedCharacter.power}
                          </div>
                        )}
                        {selectedCharacter.description && (
                          <div 
                            className="card-text card-att text-muted mt-2 mb-0"
                            style={{ backgroundImage: 'url(https://diadelavida.com/images/layout/cards_att_negative.png)' }}
                          >
                            <span>Debilidades</span><br />
                            {selectedCharacter.weakness}
                          </div>
                        )}
                        {selectedCharacter.description && (
                          <div 
                            className="card-text card-att text-muted mt-2 mb-0"
                            style={{ backgroundImage: 'url(https://diadelavida.com/images/layout/cards_att_color.png)' }}
                          >
                            <span>Color favorito</span><br />
                            {selectedCharacter.color}
                          </div>
                        )}
                        {selectedCharacter.description && (
                          <div 
                            className="card-text card-att text-muted mt-2 mb-0"
                            style={{ backgroundImage: 'url(https://diadelavida.com/images/layout/cards_att_place.png)' }}
                          >
                            <span>Vive en</span><br />
                            {selectedCharacter.place}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="position-absolute top-0 end-0 m-3 btn btn-light rounded-circle shadow-sm"
                  style={{
                    width: '40px',
                    height: '40px',
                    zIndex: 1000
                  }}
                >
                  &times;
                </button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}